import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { formatGameTimer, parseStageTimerSeconds, resolveCountdownElapsedSeconds } from '../../utils/gameTimer';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './native-places.css';

const STAGE_NUMBER = 10;
const GAME_DISPLAY_TITLE = 'Native Places';
const PARTICIPATION_POINTS = 1;
const FIELD_MAX = 120;
const MAX_NATIVE_CARDS = 20;
const DEFAULT_PLAY_SECONDS = 300;
const STAGE8_PLAY_DEADLINE_PREFIX = 'stage8_play_deadline';

const DETAIL_FIELDS = [
  { key: 'food', label: 'Food', icon: 'food' },
  { key: 'landmark', label: 'Landmark', icon: 'map' },
  { key: 'funFact', label: 'Fun Fact', icon: 'action' },
  { key: 'mustTry', label: 'Must Try', icon: 'camera' },
];

function formatStageDescription(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();
}

function parseNativePlaceDescription(text) {
  const result = {
    placeName: '',
    food: '',
    landmark: '',
    funFact: '',
    mustTry: '',
  };

  const normalizedText = formatStageDescription(text).replace(/->/g, '→').trim();
  if (!normalizedText) return result;

  const fieldMap = {
    food: 'food',
    landmark: 'landmark',
    'fun fact': 'funFact',
    'must try': 'mustTry',
  };

  // Supports one-line format like:
  // "Mumbai Food→Vada Pav Landmark→Gateway ..."
  // as well as multiline "Food→..." rows.
  const labelPattern = /(Food|Landmark|Fun Fact|Must Try)\s*(?:→|:)\s*/gi;
  const matches = [...normalizedText.matchAll(labelPattern)];

  if (matches.length > 0) {
    const placeName = normalizedText.slice(0, matches[0].index).trim();
    if (placeName) result.placeName = placeName;

    for (let i = 0; i < matches.length; i += 1) {
      const current = matches[i];
      const next = matches[i + 1];
      const key = String(current[1] ?? '').trim().toLowerCase();
      const start = (current.index ?? 0) + current[0].length;
      const end = next?.index ?? normalizedText.length;
      const value = normalizedText
        .slice(start, end)
        .replace(/\s+/g, ' ')
        .trim();
      const targetKey = fieldMap[key];
      if (targetKey) result[targetKey] = value;
    }
    return result;
  }

  const lines = normalizedText
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    const arrowMatch = line.match(/^(.+?)\s*(?:→|:)\s*(.+)$/);
    if (arrowMatch) {
      const key = arrowMatch[1].trim().toLowerCase();
      const targetKey = fieldMap[key];
      if (targetKey) result[targetKey] = arrowMatch[2].trim();
    } else if (!result.placeName) {
      result.placeName = line;
    }
  }

  return result;
}

function publicImg(name) {
  return `${process.env.PUBLIC_URL || ''}/img/${name}.png`.replace('//img', '/img');
}

function stage8PlayDeadlineKey(userId, sessionId) {
  return `${STAGE8_PLAY_DEADLINE_PREFIX}_${userId}_${sessionId}`;
}

function readPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return 0;
  try {
    const deadline = Number(sessionStorage.getItem(stage8PlayDeadlineKey(userId, sessionId)) || 0);
    return Number.isFinite(deadline) ? deadline : 0;
  } catch {
    return 0;
  }
}

function setPlayDeadline(user, deadlineMs) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage8PlayDeadlineKey(userId, sessionId), String(deadlineMs));
  } catch {
    /* ignore */
  }
}

function clearPlayDeadline(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage8PlayDeadlineKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function clearIntervalTimer(timerRef) {
  if (timerRef.current) {
    window.clearInterval(timerRef.current);
    timerRef.current = null;
  }
}

function ClockIcon({ className = 's8-timer-icon' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

function createNativeCard() {
  return {
    nativeName: '',
    food: '',
    landmark: '',
    funFact: '',
    mustTry: '',
  };
}

function isNativeCardEmpty(card) {
  return (
    !String(card.nativeName ?? '').trim() &&
    DETAIL_FIELDS.every(({ key }) => !String(card[key] ?? '').trim())
  );
}

export default function Stage8({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(8);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage8 = themeData?.stages_data?.stage_10;

  const playSeconds = useMemo(
    () => parseStageTimerSeconds(stage8?.timer, DEFAULT_PLAY_SECONDS),
    [stage8?.timer]
  );

  const logoUrl = s3PublicUrl(stage8?.logo ?? stage8?.badge);
  const rulesList = getStageRulesList(stage8);
  const subtitle = String(stage8?.title ?? stage8?.name ?? 'Around the Map We Go!');
  const descriptionText = useMemo(
    () => formatStageDescription(stage8?.description),
    [stage8?.description]
  );
  const placeGuide = useMemo(
    () => parseNativePlaceDescription(descriptionText),
    [descriptionText]
  );

  const visuals = useMemo(() => stageThemeVisuals(themeData), [themeData]);

  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's8'), [visuals]);

  const isConfigured = !!stage8 && String(stage8?.title ?? '').trim().length > 0;

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const playTimerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const [cards, setCards] = useState([createNativeCard()]);
  const [activeCard, setActiveCard] = useState(0);
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);
  const [cardAnimationKey, setCardAnimationKey] = useState(0);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const stageRulesModal = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage8,
  });
  const { showRules, openRules, closeRules } = stageRulesModal;
  const rulesDismissed = stageRulesModal.rulesDismissed;

  const countdownDisplay = timerReady ? formatGameTimer(secondsLeft) : formatGameTimer(playSeconds);
  const inputsLocked = submitted || timerExpired;

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    [],
  );

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !rulesDismissed) {
      return undefined;
    }

    const userId = user?.userId;
    const sessionId = user?.sessionId;
    if (!userId || !sessionId) return undefined;

    clearIntervalTimer(playTimerRef);

    const finishPlayTime = () => {
      clearIntervalTimer(playTimerRef);
      setSecondsLeft(0);
      setTimerExpired(true);
      clearPlayDeadline(user);
      if (!autoSubmitRef.current) {
        autoSubmitRef.current = true;
        handleSubmitRef.current?.(null, { auto: true });
      }
    };

    const startPlayCountdown = (remainingSeconds) => {
      const safeRemaining = Math.max(0, Math.ceil(Number(remainingSeconds) || 0));
      setTimerReady(true);
      setSecondsLeft(safeRemaining);
      setTimerExpired(safeRemaining <= 0);

      if (safeRemaining <= 0) {
        finishPlayTime();
        return;
      }

      let remaining = safeRemaining;
      playTimerRef.current = window.setInterval(() => {
        remaining -= 1;
        const next = Math.max(0, remaining);
        setSecondsLeft(next);
        if (next <= 0) {
          finishPlayTime();
        }
      }, 1000);
    };

    const now = Date.now();
    let playDeadline = readPlayDeadline(user);
    if (!playDeadline) {
      playDeadline = now + playSeconds * 1000;
      setPlayDeadline(user, playDeadline);
    }

    if (now >= playDeadline) {
      setTimerReady(true);
      finishPlayTime();
      return undefined;
    }

    startPlayCountdown((playDeadline - now) / 1000);

    return () => clearIntervalTimer(playTimerRef);
  }, [
    alreadyPlayed,
    isConfigured,
    playCheckReady,
    playSeconds,
    rulesDismissed,
    submitted,
    user,
    user?.userId,
    user?.sessionId,
  ]);

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const updateEntry = (index, key, value) => {
    setCards((prev) =>
      prev.map((card, i) =>
        i === index ? { ...card, [key]: String(value).slice(0, FIELD_MAX) } : card
      )
    );
    if (message) setMessage('');
  };

  const buildAnswer = () => {
    const payload = cards.map((card) => ({
      nativeName: String(card.nativeName ?? '').trim(),
      food: String(card.food ?? '').trim(),
      landmark: String(card.landmark ?? '').trim(),
      funFact: String(card.funFact ?? '').trim(),
      mustTry: String(card.mustTry ?? '').trim(),
    }));
    return JSON.stringify(payload);
  };

  const hasAllEntries = cards.every(
    (card) =>
      String(card.nativeName ?? '').trim() &&
      DETAIL_FIELDS.every(({ key }) => String(card[key] ?? '').trim())
  );

  const activeEntry = cards[activeCard] || createNativeCard();
  const canAddMoreCards = cards.length < MAX_NATIVE_CARDS;
  const canGoPrev = activeCard > 0;
  const canGoNext = activeCard < cards.length - 1 || canAddMoreCards;

  const handlePrevCard = () => {
    const current = cards[activeCard];
    const isLastCard = activeCard === cards.length - 1;
    const shouldRemove =
      activeCard > 0 && isLastCard && isNativeCardEmpty(current);

    setSlideDirection('right');
    setCardAnimationKey((prev) => prev + 1);

    if (shouldRemove) {
      setCards((prev) => prev.filter((_, i) => i !== activeCard));
      setActiveCard((prev) => Math.max(0, prev - 1));
      if (message) setMessage('');
      return;
    }

    setActiveCard((prev) => Math.max(0, prev - 1));
  };

  const handleNextCard = () => {
    if (activeCard < cards.length - 1) {
      setSlideDirection('left');
      setCardAnimationKey((prev) => prev + 1);
      setActiveCard((prev) => prev + 1);
      return;
    }
    if (!canAddMoreCards) return;
    setSlideDirection('left');
    setCardAnimationKey((prev) => prev + 1);
    setCards((prev) => [...prev, createNativeCard()]);
    setActiveCard((prev) => prev + 1);
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;

    if (!auto && !hasAllEntries) {
      triggerShake();
      setMessage('Fill all fields for every native place card');
      return;
    }

    const playDeadline = readPlayDeadline(user);
    const stageTimeSeconds = resolveCountdownElapsedSeconds({
      totalSeconds: playSeconds,
      secondsLeft,
      deadlineMs: playDeadline,
    });

    clearIntervalTimer(playTimerRef);
    clearPlayDeadline(user);

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const finalPoints = PARTICIPATION_POINTS;
    const trimmedAnswer = buildAnswer();

    if (isDemoSubscribeRedirectUser(user)) {
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: true,
        });
      }
      return;
    }

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
        timeSeconds: stageTimeSeconds,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, stageTimeSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: true,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
      });
    } catch (err) {
      setSubmitted(false);
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: true,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  return (
    <div className="stage8-page" style={pageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 10 is not configured: set stage_10 title and description in the theme.
          </p>
        }
      >
        <>
          <div className="stage-page-main stage8-header-main">
            <header className="s8-top-bar">
              <div
                className={`s8-top-timer${timerExpired ? ' s8-top-timer--expired' : ''}`}
                aria-label="Stage timer"
                aria-live="polite"
              >
                <ClockIcon />
                <span className="s8-timer-value">{countdownDisplay}</span>
              </div>
              {rulesList.length > 0 ? (
                <button
                  type="button"
                  className="stage8-rules-fab"
                  onClick={openRules}
                  aria-label="How to play"
                  title="How to play"
                >
                  ?
                </button>
              ) : null}
            </header>
          </div>

          <div className="stage-page-main stage8-main">
            <header className="s8-heading">
              <div className="s8-title-row">
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt={GAME_DISPLAY_TITLE}
                    className="s8-game-logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <h1 className="s8-game-title">{GAME_DISPLAY_TITLE}</h1>
                )}
              </div>
              {subtitle ? <p className="s8-game-subtitle">{subtitle}</p> : null}
              <span className="s8-subtitle-line" aria-hidden />
            </header>

            <div className="s8-board">
              <section className="s8-feature" aria-label="Example location details">
                <div className="s8-feature-header">Example Details</div>
                <div className="s8-feature-list">
                  <div className="s8-feature-native">
                    <span className="s8-feature-native-label">Native Place</span>
                    <strong className="s8-feature-native-value">
                      {placeGuide.placeName || 'Not set'}
                    </strong>
                  </div>
                  {DETAIL_FIELDS.map(({ key, label, icon }) => (
                    <div key={`preview-${key}`} className="s8-feature-row">
                      <img src={publicImg(icon)} alt="" className="s8-detail-icon" aria-hidden />
                      <div className="s8-detail-body">
                        <span className="s8-detail-label">{label}</span>
                        <p className="s8-feature-value">{placeGuide[key] || 'Not set'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="s8-details" aria-label="Location details">
                {!submitted ? (
                  <form className="s8-details-form" onSubmit={handleSubmit}>
                    <div className="s8-card-carousel">
                      <button
                        type="button"
                        className="s8-carousel-btn"
                        onClick={handlePrevCard}
                        disabled={inputsLocked || !canGoPrev}
                        aria-label="Previous card"
                      >
                        -
                      </button>

                      <div
                        key={`entry-card-${cardAnimationKey}-${activeCard}`}
                        className={`s8-entry-card${inputShake ? ' s8-entry-card--shake' : ''}${
                          slideDirection === 'left'
                            ? ' s8-entry-card--slide-left'
                            : slideDirection === 'right'
                              ? ' s8-entry-card--slide-right'
                              : ''
                        }`}
                      >
                        <div className="s8-entry-head">
                          <span className="s8-entry-counter">
                            Card {activeCard + 1} / {cards.length}
                          </span>
                        </div>

                        <div className="s8-native-field">
                          <label className="s8-detail-label" htmlFor={`stage8-native-${activeCard}`}>
                            Native Place
                          </label>
                          <input
                            id={`stage8-native-${activeCard}`}
                            type="text"
                            className="s8-detail-input"
                            value={activeEntry.nativeName}
                            onChange={(ev) =>
                              updateEntry(activeCard, 'nativeName', ev.target.value)
                            }
                            maxLength={FIELD_MAX}
                            placeholder="Native Place"
                            disabled={inputsLocked}
                          />
                        </div>

                        <ul className="s8-detail-list">
                          {DETAIL_FIELDS.map(({ key, label, icon }) => (
                            <li key={`${activeCard}-${key}`} className="s8-detail-row">
                              <img
                                src={publicImg(icon)}
                                alt=""
                                className="s8-detail-icon"
                                aria-hidden
                              />
                              <div className="s8-detail-body">
                                <span className="s8-detail-label">{label}</span>
                                <input
                                  id={`stage8-${activeCard}-${key}`}
                                  type="text"
                                  className="s8-detail-input"
                                  value={activeEntry[key]}
                                  onChange={(ev) => updateEntry(activeCard, key, ev.target.value)}
                                  maxLength={FIELD_MAX}
                                  placeholder={placeGuide[key] || `Enter ${label.toLowerCase()}...`}
                                  disabled={inputsLocked}
                                  aria-label={label}
                                />
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <button
                        type="button"
                        className="s8-carousel-btn s8-carousel-btn--add"
                        onClick={handleNextCard}
                        disabled={inputsLocked || !canGoNext}
                        aria-label={activeCard < cards.length - 1 ? 'Next card' : 'Add new card'}
                      >
                        +
                      </button>
                    </div>

                    <button
                      type="submit"
                      className="s8-submit-btn"
                      disabled={inputsLocked || !hasAllEntries}
                    >
                      Submit
                    </button>
                  </form>
                ) : (
                  <p className="s8-complete">Thanks! Your travel guide has been submitted.</p>
                )}

                {message ? <div className="s8-hint">{message}</div> : null}
              </section>

              <div className="stage-page-puzzle-aside stage8-puzzle-aside">
                <StagePuzzleReveal themeData={themeData} />
              </div>
            </div>
          </div>
        </>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage8-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage8-rules-title"
          onClick={closeRules}
        >
          <div className="stage8-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage8-rules-title">How to play</h2>
            <ul className="stage8-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage8-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRevealOverlay ? (
        <div
          className="stage-reveal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Revealing your puzzle progress"
          onClick={handleRevealOverlayClick}
        >
          <div className="stage-reveal-confetti" aria-hidden>
            {revealConfettiPieces.map((piece) => (
              <span
                key={piece.id}
                className="stage-reveal-confetti-piece"
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                }}
              />
            ))}
          </div>
          <div className="stage-reveal-stage">
            <aside className="stage-reveal-panel" aria-label="Puzzle pieces progress">
              <StagePuzzleReveal
                themeData={themeData}
                completedDaysOverride={revealCompletedDays}
                animateReveal
              />
            </aside>
          </div>
        </div>
      ) : null}
    </div>
  );
}
